/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — headless capture sheet.

   Mounts a second, off-screen `<ReactFlow>` instance (same
   nodeTypes/edgeTypes/data as the live canvas) sized to the full
   node/edge bounding box, and reports "ready to rasterize" only once
   every node is measured AND every edge has actually painted a path —
   root cause #3 fix (the old dialog waited a FIXED 2×rAF+120ms with no
   measured-nodes gate, so edges could still be null on cold mounts).

   `OrgEdgeV2` (the new edge renderer) reads snap/canEdit state from
   `OrgChartCanvasContext` — this headless tree needs its own (inert)
   provider or the hook throws.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import { ConnectionMode, ReactFlow, ReactFlowProvider, useNodesInitialized } from '@xyflow/react';
import type { Node, Edge, Viewport } from '@xyflow/react';
import type { Team } from '../../../../../shared/types';
import { nodeTypes, LeaderBadge } from '../nodes';
import { edgeTypes } from '../edges/OrgEdgeV2';
import { OrgChartCanvasProvider } from '../context/OrgChartCanvasContext';
import type { OrgChartCanvasContextValue } from '../context/OrgChartCanvasContext';
import { DEFAULT_NODE_STYLE } from '../types';
import { computeChartLegend } from '../legend';
import { HEADER_H_PX, LEGEND_H_PX, MARGIN_PX, todayLabel } from './geometry';

export interface CaptureJob {
  widthPx: number;
  heightPx: number;
  viewport: Viewport;
  nodes: Node[];
  edges: Edge[];
  title: string;
  firmName: string;
  showLegend: boolean;
  teams: Team[];
}

// Inert canvas context for the headless tree — print is read-only, nothing
// here ever drags/snaps/edits, so a static dummy is correct and cheap.
const CAPTURE_CANVAS_CONTEXT: OrgChartCanvasContextValue = {
  canEdit: false,
  snapEnabled: false,
  snapTargetsRef: { current: { xs: [], ys: [] } },
  nodeStyle: DEFAULT_NODE_STYLE,
};

// Hard ceiling so a pathological mount (e.g. an edge type that never
// paints) can't hang the print dialog forever — capture proceeds anyway
// past this point rather than leaving the user stuck on "Preparing…".
const CAPTURE_READY_TIMEOUT_MS = 15000;

/** True once every non-hidden node is measured AND (when edges exist) the
 *  edges layer has painted at least one `<path class="react-flow__edge-path">`
 *  per edge. */
function useCaptureReady(
  containerRef: React.RefObject<HTMLElement>,
  expectedEdgeCount: number
): boolean {
  const nodesInitialized = useNodesInitialized({ includeHiddenNodes: false });
  const [edgesPainted, setEdgesPainted] = useState(expectedEdgeCount === 0);

  useEffect(() => {
    if (expectedEdgeCount === 0) {
      setEdgesPainted(true);
      return;
    }
    setEdgesPainted(false);
    let raf = 0;
    let cancelled = false;
    const start = performance.now();
    const poll = () => {
      if (cancelled) return;
      const el = containerRef.current;
      const count = el ? el.querySelectorAll('.react-flow__edge-path').length : 0;
      if (count >= expectedEdgeCount || performance.now() - start > CAPTURE_READY_TIMEOUT_MS) {
        setEdgesPainted(true);
        return;
      }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedEdgeCount]);

  return nodesInitialized && edgesPainted;
}

function CaptureCanvas({ job, onReady }: { job: CaptureJob; onReady: () => void }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const ready = useCaptureReady(wrapRef, job.edges.length);
  const firedRef = useRef(false);

  useEffect(() => {
    if (ready && !firedRef.current) {
      firedRef.current = true;
      onReady();
    }
  }, [ready, onReady]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={job.nodes}
        edges={job.edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        // Same as the live canvas — without Loose, target-handle lookups on
        // our all-source handles fail (#008) and no edge ever paints.
        connectionMode={ConnectionMode.Loose}
        defaultViewport={job.viewport}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
        style={{ background: '#ffffff' }}
      />
    </div>
  );
}

/** Full off-screen sheet: title/firm header, the canvas, and an optional
 *  team-color legend strip — behavior/layout ported verbatim from the old
 *  dialog. `sheetRef` is the element `html-to-image` rasterizes. */
export const CaptureSheet = React.forwardRef<
  HTMLDivElement,
  { job: CaptureJob; onReady: () => void }
>(function CaptureSheet({ job, onReady }, sheetRef) {
  return (
    <div
      ref={sheetRef}
      style={{
        width: job.widthPx,
        height: job.heightPx,
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        padding: MARGIN_PX,
        boxSizing: 'border-box',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          height: HEADER_H_PX,
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: 30, fontWeight: 700, color: '#111827' }}>{job.title}</div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{job.firmName}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Printed {todayLabel()}</div>
        </div>
      </div>

      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <ReactFlowProvider>
          <OrgChartCanvasProvider value={CAPTURE_CANVAS_CONTEXT}>
            <CaptureCanvas job={job} onReady={onReady} />
          </OrgChartCanvasProvider>
        </ReactFlowProvider>
      </div>

      {job.showLegend &&
        (() => {
          // Badge legend (Josh grant 2026-08-18) — same single source
          // of truth as the on-canvas `LegendPopover`, computed off
          // this SAME job.nodes array so it can never drift from what
          // the printed cards actually show. Additive: the existing
          // per-team color strip above is untouched; badge entries
          // just join the same wrapping row. `minHeight` (not a fixed
          // `height`) so a firm with many badge kinds grows this
          // section instead of clipping it — the canvas area above
          // (`flex: 1`) yields the room.
          const badgeLegend = computeChartLegend(job.nodes);
          return (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 16,
                alignItems: 'center',
                minHeight: LEGEND_H_PX,
                borderTop: '1px solid #e5e7eb',
                paddingTop: 12,
                flexShrink: 0,
              }}
            >
              {job.teams.map((t) => (
                <div
                  key={t.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: t.color || '#9ca3af',
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ color: '#374151' }}>{t.name}</span>
                </div>
              ))}
              {badgeLegend.leader && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  {/* Same LeaderBadge component every card renders — see
                      LegendPopover for why the color is neutral here. */}
                  <LeaderBadge color="#9ca3af" />
                  <span style={{ color: '#374151' }}>{badgeLegend.leader.label}</span>
                </div>
              )}
              {badgeLegend.titleBadges.map((b) => (
                <div
                  key={b.key}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 20,
                      height: 16,
                      padding: '0 5px',
                      fontSize: 10,
                      fontWeight: 800,
                      borderRadius: 4,
                      background: '#ffffff',
                      color: b.color,
                      border: `1.5px solid ${b.color}`,
                    }}
                  >
                    {b.code}
                  </span>
                  <span style={{ color: '#374151' }}>{b.label}</span>
                </div>
              ))}
              {badgeLegend.departmentPills.map((p) => (
                <div
                  key={p.key}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 20,
                      height: 16,
                      padding: '0 5px',
                      fontSize: 10,
                      fontWeight: 800,
                      borderRadius: 4,
                      background: p.color + '18',
                      color: p.color,
                      border: `1px solid ${p.color}40`,
                    }}
                  >
                    {p.code}
                  </span>
                  <span style={{ color: '#374151' }}>{p.label}</span>
                </div>
              ))}
              {badgeLegend.inactive && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: '#d1d5db',
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ color: '#374151' }}>{badgeLegend.inactive.label}</span>
                </div>
              )}
            </div>
          );
        })()}
    </div>
  );
});
